import React, { useState } from 'react';
import { Box, IconButton, Link, TextField, Typography } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import { DropdownFilter } from '../../../components/Planner/Filters/DropdownFilter';
import globals from '../../../globals/communityGlobals';
import KeyboardDateSelector from '../../../components/common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps } from '../../../utils/keyboardSelectorFunctions';
import { isAnyDateEmpty, isDateFromBeforeDateTo } from '../../../utils/dates';

const locale = globals.getArea('patients');
const maxChars = 150;
const defaultState = {
  from: new Date(globals.now.subtract(3, 'months').format(globals.dateFormats.date)),
  to: new Date(globals.now.format(globals.dateFormats.date)),
  forename: '',
  surname: '',
  nhsNumber: '',
  dateOfBirth: '',
  auditType: 0,
};

const auditTypes = [{ id: 0, name: 'Patient Audit' }, { id: 1, name: 'Unsuccessful Sync Patients' }];

export default function PatientAuditSearchFilters({ onSubmit, errorProps }) {
  const [state, setState] = useState(defaultState);
  const [validationErrors, setValidationErrors] = useState({});

  const handleClear = () => {
    setState(defaultState);
    setValidationErrors({});
    onSubmit(defaultState);
  };

  const handleDateChange = (fieldName, date) => {
    setState({ ...state, [fieldName]: date });

    const from = fieldName === 'from' ? date : state.from;
    const to = fieldName === 'to' ? date : state.to;
    const emptyFieldErrorMessage = 'Please enter date';

    if (isAnyDateEmpty(from, to)) {
      setValidationErrors({
        from: from ? null : emptyFieldErrorMessage,
        to: to ? null : emptyFieldErrorMessage,
      });
      return;
    }

    setValidationErrors({ from: null, to: null });
  };

  const noValidationErrors = () => {
    return !validationErrors.from && !validationErrors.to;
  };

  const areDatesValid = noValidationErrors() && isDateFromBeforeDateTo(state.from, state.to, true);

  return (
    <form
      className="slick-page-header-section"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(state);
      }}
      noValidate
      autoComplete="off"
    >
      <Box className="filter-section" display="flex" p={0} justifyContent="flex-start" px={1}>
        <Box p={1}>
          <FilterListIcon />
        </Box>
        <Box px={1} display="flex" alignItems="center" flexGrow={1}>
          <Box p={0} display="flex" textAlign="end">
            <Typography className="ellipsis mx-display-flex" variant="body1">
              Type
            </Typography>
          </Box>
          <Box p={1} flexGrow={1} width={170}>
            <DropdownFilter
              data={auditTypes}
              defaultValue={[auditTypes[0].id]}
              filterKey={'type'}
              filterValue={state.auditType}
              onChange={(name, value) => setState({ ...state, auditType: value })}
            />
          </Box>
        </Box>
        {state.auditType === 0 && (
          <React.Fragment>
            <Box px={1} display="flex" alignItems="center" flexGrow={1}>
              <Box p={0} display="flex" textAlign="end">
                <Typography className="ellipsis mx-display-flex" variant="body1">
                  {locale.getMsg('forename')}
                </Typography>
              </Box>
              <Box p={1} flexGrow={1}>
                <TextField
                  value={state.forename}
                  fullWidth
                  onChange={e => setState({ ...state, forename: e.target.value })}
                  inputProps={{
                    maxLength: maxChars,
                  }}
                />
              </Box>
            </Box>
            <Box px={1} display="flex" alignItems="center" flexGrow={1}>
              <Box p={0} display="flex" textAlign="end">
                <Typography className="ellipsis mx-display-flex" variant="body1">
                  {locale.getMsg('surname')}
                </Typography>
              </Box>
              <Box p={1} flexGrow={1}>
                <TextField
                  value={state.surname}
                  fullWidth
                  onChange={e => setState({ ...state, surname: e.target.value })}
                  inputProps={{
                    maxLength: maxChars,
                  }}
                />
              </Box>
            </Box>
            <Box px={1} display="flex" alignItems="center" flexGrow={1}>
              <Box p={0} display="flex" textAlign="end">
                <Typography className="ellipsis mx-display-flex" variant="body1">
                  {locale.getMsg('nhsNumber')}
                </Typography>
              </Box>
              <Box p={1} flexGrow={1}>
                <TextField
                  value={state.nhsNumber}
                  fullWidth
                  error={errorProps.includes('nhsNumber')}
                  onChange={e => setState({ ...state, nhsNumber: e.target.value })}
                />
              </Box>
            </Box>
          </React.Fragment>
        )}
        <Box px={1} display="flex" alignItems="center" flexGrow={1} style={{ width: '13%' }}>
          <Box p={0} display="flex" textAlign="end">
            <Typography className="ellipsis mx-display-flex" variant="body1">
              {locale.getMsg('from')}
            </Typography>
          </Box>
          <Box p={1} flexGrow={1} style={{ marginTop: '6px' }}>
            <KeyboardDateSelector
              value={state.from}
              onChange={date => handleDateChange('from', date)}
              maxDate={state.to}
              maxDateMessage=""
              errorProps={getErrorProps(validationErrors.from)}
            />
          </Box>
        </Box>
        <Box px={1} display="flex" alignItems="center" flexGrow={1} style={{ width: '13%' }}>
          <Box p={0} display="flex" textAlign="end">
            <Typography className="ellipsis mx-display-flex" variant="body1">
              {locale.getMsg('to')}
            </Typography>
          </Box>
          <Box p={1} flexGrow={1} style={{ marginTop: '6px' }}>
            <KeyboardDateSelector
              value={state.to}
              onChange={date => handleDateChange('to', date)}
              minDate={state.from}
              minDateMessage="Date to cannot be before date from."
              errorProps={getErrorProps(validationErrors.to)}
            />
          </Box>
        </Box>
        <Box p={1}>
          <Link className="cancel-link" onClick={handleClear}>
            {locale.getMsg('clear')}
          </Link>
        </Box>
        <Box p={1}>
          <IconButton type="submit" size="small" className="search-icon" disabled={!areDatesValid}>
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>
    </form>
  );
}
